import type { ReactNode } from 'react'
import styles from './Tabs.module.css'

export interface TabDefinition<T extends string> {
  id: T
  label: string
  /** Optional count or state shown after the label — "3", "Working". */
  hint?: string | undefined
}

export interface TabsProps<T extends string> {
  /** Names the set for a screen reader: "Images", "Encounter sides". */
  label: string
  tabs: readonly TabDefinition<T>[]
  active: T
  onSelect: (id: T) => void
}

/**
 * One row of tabs, switching between panels on the same screen.
 *
 * The alternative on a phone is a long scroll with several cards stacked, and that is the right
 * answer when the cards are *parts of one thing* — a character sheet is fourteen sections and
 * tabbing it would hide half a character. Tabs are for when the panels are alternatives: you
 * are looking at pictures, or you are making one, and never both at once.
 *
 * Built as a real ARIA tablist rather than styled buttons, so arrow keys move between tabs and
 * a screen reader announces "tab 2 of 2" instead of reading out two unrelated buttons. Roving
 * `tabIndex` is what makes Tab itself skip to the panel rather than walking through every tab.
 */
export function Tabs<T extends string>({ label, tabs, active, onSelect }: TabsProps<T>) {
  const index = tabs.findIndex((tab) => tab.id === active)

  const move = (delta: number) => {
    const next = tabs[(index + delta + tabs.length) % tabs.length]
    if (next !== undefined) onSelect(next.id)
  }

  return (
    <div className={styles.tabs} role="tablist" aria-label={label}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          id={`tab-${tab.id}`}
          aria-selected={tab.id === active}
          aria-controls={`panel-${tab.id}`}
          tabIndex={tab.id === active ? 0 : -1}
          className={`${styles.tab} ${tab.id === active ? styles.tabActive : ''}`}
          onClick={() => onSelect(tab.id)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowRight') move(1)
            if (event.key === 'ArrowLeft') move(-1)
          }}
        >
          {tab.label}
          {tab.hint !== undefined && <span className={styles.hint}>{tab.hint}</span>}
        </button>
      ))}
    </div>
  )
}

/** The panel one tab reveals. Rendered only when its tab is the active one. */
export function TabPanel({ id, children }: { id: string; children: ReactNode }) {
  return (
    <div id={`panel-${id}`} role="tabpanel" aria-labelledby={`tab-${id}`} tabIndex={-1}>
      {children}
    </div>
  )
}
